import { useState } from 'react';
import { db } from '../firebase';
import { ref, update } from 'firebase/database';

type ButtonType = 'chat' | 'whatsapp' | 'phone' | 'email';

interface ContactButton {
  type: ButtonType;
  label: string;
  value: string;
  enabled: boolean;
}

interface WidgetConfig {
  color: string;
  buttons: ContactButton[];
}

const TYPE_META: Record<ButtonType, { icon: string; color: string; name: string; valuePlaceholder: string; valueLabel: string }> = {
  chat:     { icon: '💬', color: '#5B4FE8', name: 'Live Chat',  valuePlaceholder: '',                     valueLabel: '' },
  whatsapp: { icon: '🟢', color: '#25D366', name: 'WhatsApp',   valuePlaceholder: '+263 78 878 5868',     valueLabel: 'WhatsApp number' },
  phone:    { icon: '📞', color: '#0ea5a4', name: 'Phone Call', valuePlaceholder: '+263 78 878 5868',     valueLabel: 'Phone number' },
  email:    { icon: '✉️', color: '#3b82f6', name: 'Email',      valuePlaceholder: 'saas@webiconic.co.zw', valueLabel: 'Email address' },
};

const DEFAULT_BUTTONS: ContactButton[] = [
  { type: 'chat',     label: 'Chat with us', value: '', enabled: true },
  { type: 'whatsapp', label: 'WhatsApp',     value: '', enabled: false },
  { type: 'phone',    label: 'Call us',      value: '', enabled: false },
  { type: 'email',    label: 'Email us',     value: '', enabled: false },
];

interface Props {
  botId: string;
  initialConfig?: WidgetConfig | null;
}

export default function WidgetCustomizationPanel({ botId, initialConfig }: Props) {
  const [color, setColor] = useState(initialConfig?.color || '#5B4FE8');
  const [buttons, setButtons] = useState<ContactButton[]>(
    initialConfig?.buttons?.length ? initialConfig.buttons : DEFAULT_BUTTONS
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  function updateButton(type: ButtonType, patch: Partial<ContactButton>) {
    setButtons(prev => prev.map(b => (b.type === type ? { ...b, ...patch } : b)));
    setSaved(false);
  }

  async function save() {
    const missingValue = buttons.find(b => b.enabled && b.type !== 'chat' && !b.value.trim());
    if (missingValue) {
      alert(`Add a ${TYPE_META[missingValue.type].valueLabel.toLowerCase()} for the ${TYPE_META[missingValue.type].name} button, or turn it off.`);
      return;
    }
    setSaving(true);
    try {
      await update(ref(db, `bots/${botId}`), { widgetConfig: { color, buttons } });
      setSaved(true);
    } catch {
      alert('Could not save — check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  const enabledCount = buttons.filter(b => b.enabled).length;

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '24px' }}>
      <div style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>Customize the widget popup</div>
      <div style={{ color: 'var(--text-faint)', fontSize: '12px', marginBottom: '20px' }}>
        {enabledCount <= 1 && buttons.find(b => b.type === 'chat')?.enabled
          ? 'Right now this shows as a single chat bubble. Turn on more options below to show a contact menu instead.'
          : 'Visitors will see an expandable menu with these options.'}
      </div>

      <div style={{ marginBottom: '22px' }}>
        <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Accent color</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input type="color" value={color} onChange={e => { setColor(e.target.value); setSaved(false); }}
            style={{ width: '42px', height: '38px', padding: '2px', border: '1px solid var(--card-border)', borderRadius: '8px', background: 'var(--bg-page)', cursor: 'pointer' }} />
          <input type="text" value={color} onChange={e => { setColor(e.target.value); setSaved(false); }} placeholder="#5B4FE8"
            style={{ width: '120px', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none' }} />
        </div>
      </div>

      <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
        Contact options shown on your site
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
        {buttons.map(b => {
          const meta = TYPE_META[b.type];
          return (
            <div key={b.type} style={{ padding: '12px', background: 'var(--bg-page)', borderRadius: '10px', border: '1px solid var(--card-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: b.enabled && b.type !== 'chat' ? '10px' : 0 }}>
                <span style={{ width: '30px', height: '30px', borderRadius: '50%', background: meta.color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>{meta.icon}</span>
                <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '600', width: '90px', flexShrink: 0 }}>{meta.name}</div>
                <input
                  value={b.label}
                  onChange={e => updateButton(b.type, { label: e.target.value })}
                  placeholder="Label shown to visitors"
                  disabled={!b.enabled}
                  style={{ flex: 1, padding: '8px 10px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-primary)', fontSize: '12px', outline: 'none', opacity: b.enabled ? 1 : 0.5 }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', flexShrink: 0 }}>
                  <input type="checkbox" checked={b.enabled} onChange={e => updateButton(b.type, { enabled: e.target.checked })} />
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{b.enabled ? 'On' : 'Off'}</span>
                </label>
              </div>
              {b.enabled && b.type !== 'chat' && (
                <input
                  value={b.value}
                  onChange={e => updateButton(b.type, { value: e.target.value })}
                  placeholder={meta.valuePlaceholder}
                  style={{ width: '100%', padding: '8px 10px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-primary)', fontSize: '12px', outline: 'none', boxSizing: 'border-box', marginLeft: '40px', maxWidth: 'calc(100% - 40px)' }}
                />
              )}
            </div>
          );
        })}
      </div>

      <button onClick={save} disabled={saving}
        style={{ background: '#5B4FE8', border: 'none', color: 'white', padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
        {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save widget settings'}
      </button>
      <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '8px' }}>
        Changes apply the next time the widget loads on your site (visitors already on the page need to refresh).
      </div>
    </div>
  );
}
